import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button, Icon, Select, TimePicker
} from 'antd';
import {
  ANY, MAX_WINDOWS, parseAvailableSignOn, toUiState, toUtcValue
} from 'library/availableSignOn';

const TIME_FORMAT = 'HH:mm';
const HELP = {
  any: 'Available for any sign-on time on this date.',
  ds: 'Day shift sign-on window.',
  ns: 'Night shift sign-on window, crosses midnight.'
};

let nextId = 0;
const newId = () => {
  nextId += 1;
  return nextId;
};
const newRow = (start = '', end = '') => ({ key: newId(), start, end });
const dash = value => value.replace('-', '–');
const toMoment = (text) => {
  const time = moment(text, TIME_FORMAT, true);
  return time.isValid() ? time : null;
};
const check = value => parseAvailableSignOn(value, { requireHalfHour: true });

const composeValue = (mode, rows, presets) => {
  if (mode === 'any') {
    return ANY;
  }
  if (mode === 'ds' || mode === 'ns') {
    return presets[mode];
  }
  if (mode === 'custom') {
    return rows.map(row => `${row.start}-${row.end}`).join(';');
  }
  return null;
};

const initialState = (value, presets, mixed) => {
  if (mixed) {
    return { mode: undefined, rows: [], error: null };
  }
  const state = toUiState(value, presets);
  return { ...state, rows: state.rows.map(row => newRow(row.start, row.end)) };
};

const AvailableSignOnField = forwardRef(({
  anchor, autoFocus, disabled, getPopupContainer, mixed, onChange, presets, showError, size, value
}, ref) => {
  const [state, setState] = useState(() => initialState(value, presets, mixed));
  const [errorId] = useState(() => `available-sign-on-error-${newId()}`);

  const current = composeValue(state.mode, state.rows, presets);
  const parsed = check(current);
  // A legacy invalid value is always shown; rule errors only when showError is on.
  const error = state.error || (showError ? parsed.error : null);

  const update = (mode, rows) => {
    setState({ mode, rows, error: null });
    const next = composeValue(mode, rows, presets);
    onChange(next, !check(next).error);
  };

  const handleModeChange = (mode = null) => {
    if (mode !== 'custom') {
      update(mode, []);
    }
    else if (state.mode === 'ds' || state.mode === 'ns') {
      const [start, end] = presets[state.mode].split('-');
      update('custom', [newRow(start, end)]);
    }
    else {
      update('custom', [newRow()]);
    }
  };

  const setRow = (index, key, text) => update(
    'custom',
    state.rows.map((row, i) => (i === index ? { ...row, [key]: text } : row))
  );
  const removeRow = index => update(
    'custom',
    state.rows.length === 1 ? [newRow()] : state.rows.filter((row, i) => i !== index)
  );

  let savesAs = 'Not set';
  if (parsed.value === ANY) {
    savesAs = ANY;
  }
  else if (parsed.value) {
    savesAs = anchor ? `${parsed.value} (${toUtcValue(parsed.value, anchor)} UTC)` : parsed.value;
  }
  else if (current) {
    savesAs = current;
  }

  const timePicker = (row, index, key) => (
    <TimePicker
      className="bhp-sign-on-field__time"
      format={TIME_FORMAT}
      minuteStep={30}
      placeholder="--:--"
      size={size === 'small' ? 'small' : 'default'}
      disabled={disabled}
      getPopupContainer={getPopupContainer}
      value={toMoment(row[key])}
      onChange={(time, text) => setRow(index, key, text)}
    />
  );

  return (
    <div
      ref={ref}
      className={`bhp-sign-on-field bhp-sign-on-field--${size}`}
      role="group"
      aria-label="Available sign-on"
      aria-describedby={error ? errorId : undefined}
    >
      <Select
        allowClear
        autoFocus={autoFocus}
        disabled={disabled}
        size={size}
        value={state.mode || undefined}
        placeholder={mixed && state.mode === undefined ? 'Mixed' : 'Not set'}
        getPopupContainer={getPopupContainer}
        onChange={handleModeChange}
        style={{ width: '100%' }}
      >
        <Select.Option value="any">Any sign-on</Select.Option>
        <Select.Option value="ds">{`Day shift only — ${dash(presets.ds)}`}</Select.Option>
        <Select.Option value="ns">{`Night shift only — ${dash(presets.ns)}`}</Select.Option>
        <Select.Option value="custom">Custom window(s)…</Select.Option>
      </Select>
      {HELP[state.mode] && <div className="bhp-sign-on-field__help">{HELP[state.mode]}</div>}
      {state.mode === 'custom' && (
        <div className="bhp-sign-on-field__custom">
          {state.rows.map((row, index) => (
            <div
              key={row.key}
              className={`bhp-sign-on-field__row${check(`${row.start}-${row.end}`).error ? ' has-error' : ''}`}
            >
              {timePicker(row, index, 'start')}
              <span className="bhp-sign-on-field__arrow" aria-hidden="true">→</span>
              {timePicker(row, index, 'end')}
              <Button
                size="small"
                icon="close"
                disabled={disabled}
                aria-label={`Remove window ${index + 1}`}
                onClick={() => removeRow(index)}
              />
            </div>
          ))}
          {state.rows.length < MAX_WINDOWS && (
            <Button
              className="bhp-sign-on-field__add"
              size="small"
              type="dashed"
              icon="plus"
              disabled={disabled}
              onClick={() => update('custom', [...state.rows, newRow()])}
            >
              Add window
            </Button>
          )}
        </div>
      )}
      <div className="bhp-sign-on-field__saves-as">
        Saves as
        {' '}
        <code>{savesAs}</code>
      </div>
      <div id={errorId} className="bhp-sign-on-field__error" aria-live="polite">
        {error && (
          <span>
            <Icon type="close-circle" theme="filled" />
            {' '}
            {error.message}
          </span>
        )}
      </div>
    </div>
  );
});

AvailableSignOnField.propTypes = {
  anchor: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]),
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  getPopupContainer: PropTypes.func,
  mixed: PropTypes.bool,
  onChange: PropTypes.func,
  presets: PropTypes.shape({ ds: PropTypes.string, ns: PropTypes.string }).isRequired,
  showError: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'small']),
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.string
};

AvailableSignOnField.defaultProps = {
  anchor: null,
  autoFocus: false,
  disabled: false,
  getPopupContainer: undefined,
  mixed: false,
  onChange() {
  },
  showError: true,
  size: 'default'
};

export default AvailableSignOnField;
